import * as React from 'react';
import Box from '@mui/material/Box';
import { tokens } from '../DashboardTheme/DashboardTheme';

const SPEED_PX_PER_SEC = 55;

/**
 * Infinite horizontal marquee.
 *
 * The track holds two identical halves and slides to -50%, so the loop is
 * seamless only when one half is at least as wide as the container. A single
 * pass of `items` is usually narrower than the viewport, which leaves a visible
 * gap at the end of each cycle — so the list is repeated `groupsPerHalf` times
 * until one half covers the container. Duration is derived from the measured
 * width to keep the scroll speed constant at any repeat count.
 */
export default function Ticker({
    items,
    background = tokens.red,
    color = '#FFFFFF',
    fontSize = { xs: 20, md: 28 },
    py = 1.4,
    borderY = true,
}) {
    const containerRef = React.useRef(null);
    const groupRef = React.useRef(null);
    const [groupsPerHalf, setGroupsPerHalf] = React.useState(1);
    const [duration, setDuration] = React.useState(30);

    React.useLayoutEffect(() => {
        const measure = () => {
            const container = containerRef.current;
            const group = groupRef.current;
            if (!container || !group) return;

            const groupWidth = group.getBoundingClientRect().width;
            const containerWidth = container.getBoundingClientRect().width;
            if (!groupWidth || !containerWidth) return;

            const needed = Math.max(1, Math.ceil(containerWidth / groupWidth));
            setGroupsPerHalf(needed);
            setDuration(Math.max(12, (groupWidth * needed) / SPEED_PX_PER_SEC));
        };

        measure();

        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', measure);
            return () => window.removeEventListener('resize', measure);
        }
        const observer = new ResizeObserver(measure);
        if (containerRef.current) observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, [items]);

    // Two halves of `groupsPerHalf` passes each.
    const groups = Array.from({ length: groupsPerHalf * 2 });

    return (
        <Box
            className="vg-ticker"
            ref={containerRef}
            sx={{
                background,
                py,
                borderTop: borderY ? `1px solid ${tokens.lineStrong}` : 'none',
                borderBottom: borderY ? `1px solid ${tokens.lineStrong}` : 'none',
            }}
        >
            <Box className="vg-ticker__track" style={{ animationDuration: `${duration}s` }}>
                {groups.map((_, groupIndex) => (
                    <Box
                        key={groupIndex}
                        ref={groupIndex === 0 ? groupRef : null}
                        aria-hidden={groupIndex > 0}
                        sx={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
                    >
                        {items.map((item, index) => (
                            <Box
                                key={`${item}-${index}`}
                                component="span"
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '2.5rem',
                                    pr: '2.5rem',
                                    fontFamily: tokens.display,
                                    fontSize,
                                    letterSpacing: '0.06em',
                                    color,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {item}
                                <Box
                                    component="span"
                                    aria-hidden
                                    sx={{
                                        width: 7,
                                        height: 7,
                                        borderRadius: '50%',
                                        backgroundColor: color,
                                        opacity: 0.65,
                                        flexShrink: 0,
                                    }}
                                />
                            </Box>
                        ))}
                    </Box>
                ))}
            </Box>
        </Box>
    );
}
